import React, { useState, useEffect } from "react";
import Slider from "react-slick";
import { createPortal } from "react-dom";

export default function Speakers({
  className,
  speakers,
  verticalOffset,
  noModal,
  colWidth,
}) {
  const [selectedSpeaker, setSelectedSpeaker] = useState(null);
  const [isVisible, setIsVisible] = useState(false);

  const openModal = (speaker) => {
    setSelectedSpeaker(speaker);
    setTimeout(() => setIsVisible(true), 10);
    document.body.style.overflow = "hidden";
  };

  const closeModal = () => {
    setIsVisible(false);
    setTimeout(() => setSelectedSpeaker(null), 300);
    document.body.style.overflow = "auto";
  };

  const getSpeakerImage = (name, isFeatured = false) => {
    const formattedName = (name ?? "n-a")
      .toLowerCase()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-");

    const baseName = `portrait-${formattedName}${
      isFeatured ? "-featured" : ""
    }.webp`;
    const imagePath = `/img/${baseName}`;
    const img = new Image();
    img.src = imagePath;

    return new Promise((resolve) => {
      img.onload = () => resolve(imagePath);
      img.onerror = () => resolve("/img/portrait-tba.webp");
    });
  };

  const [speakerImages, setSpeakerImages] = useState({});

  useEffect(() => {
    const loadImages = async () => {
      const images = {};
      for (const speaker of speakers) {
        const name = speaker.name ?? "n-a";
        const imgPath = await getSpeakerImage(name, speaker.isFeatured);
        images[name] = imgPath;
      }
      setSpeakerImages(images);
    };
    loadImages();
  }, [speakers]);

  return (
    <section className={`${className}`}>
      <div
        className="container"
        style={{ marginTop: verticalOffset ? "-7em" : "0" }}
      >
        <div className="row pb-5">
          {speakers.map((speaker, index) => (
            <div
              key={index}
              className={`px-2 py-3 ${speaker.classes} ${
                speaker.isFeatured
                  ? "col-12 col-lg-6"
                  : `col-6 col-lg-${colWidth || 4}`
              }`}
            >
              <div
                className={`speaker-card ${
                  speaker.isFeatured ? "speaker-card-featured" : ""
                } ${speaker.invisible ? "speaker-card-invisible" : ""}
                  ${noModal ? "speaker-card-no-modal" : ""}`}
              >
                <div className="speaker-card-inner">
                  <div
                    className="speaker-image"
                    style={{
                      backgroundImage: `url(${
                        speakerImages[speaker.name] || "/img/portrait-tba.webp"
                      })`,
                    }}
                    onClick={() => !noModal && openModal(speaker)}
                  ></div>

                  <div className="contents">
                    <div className="info">
                      {speaker.isFeatured && (
                        <div className="feature-tag">Keynote Speaker</div>
                      )}
                      {speaker.isFeatured ? (
                        <h2>{speaker.name ?? "N/A"}</h2>
                      ) : (
                        <h3>{speaker.name ?? "N/A"}</h3>
                      )}
                      <p>{speaker.role ?? "N/A"}</p>
                    </div>
                    {Array.isArray(speaker.ytData) &&
                      speaker.ytData.length > 0 && (
                        <div className="yt-data">
                          {speaker.ytData.map((card, ytIndex) => (
                            <a
                              key={`${index}-${ytIndex}`}
                              href={`https://www.youtube.com/watch?v=${card.id}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="yt-link"
                            >
                              <i className="fa fa-play-circle"></i>
                              <span>{card.title ?? "Watch Video"}</span>
                            </a>
                          ))}
                        </div>
                      )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {selectedSpeaker &&
        createPortal(
          <div
            className={`user-overlay ${isVisible ? "visible" : ""}`}
            onClick={closeModal}
          >
            <div className="modal-inner" onClick={(e) => e.stopPropagation()}>
              <div className="container">
                <div className="row text-end pb-3">
                  <div className="col">
                    <div className="close-btn" onClick={closeModal}>
                      Close <i className="fa fa-times"></i>
                    </div>
                  </div>
                </div>
                <div className="row justify-content-center py-5">
                  <div className="col-8 col-md-4 mb-4 pe-5">
                    <img
                      className="img-fluid"
                      src={`/img/portrait-${(selectedSpeaker?.name ?? "n-a")
                        .toLowerCase()
                        .replace(/[^\w\s-]/g, "")
                        .replace(/\s+/g, "-")}.webp`}
                      alt={selectedSpeaker?.name ?? "N/A"}
                      onError={(e) => {
                        e.target.src = "/img/portrait-tba.webp";
                      }}
                    />
                  </div>
                  <div className="col-12 col-md-8">
                    <h1 className="color-white mb-0">
                      {selectedSpeaker?.name ?? "N/A"}
                    </h1>
                    <strong className="color-white mb-5 d-block">
                      {selectedSpeaker?.role ?? "N/A"}
                    </strong>
                    <p
                      className=" color-white"
                      dangerouslySetInnerHTML={{
                        __html: selectedSpeaker?.description ?? "N/A",
                      }}
                    ></p>
                  </div>
                </div>
              </div>
            </div>
          </div>,
          document.getElementById("modal-container")
        )}
    </section>
  );
}
export function SpeakerCardSlider({ className, data, title }) {
  const settings = {
    infinite: true,
    slidesToShow: 3,
    slidesToScroll: 3,
    dots: true,
    prevArrow: (
      <div className="slick-prev">
        <i className="fa-solid fa-circle-arrow-left"></i>
      </div>
    ),
    nextArrow: (
      <div className="slick-next">
        <i className="fa-solid fa-circle-arrow-right"></i>
      </div>
    ),
    responsive: [
      {
        breakpoint: 1024,
        settings: {
          slidesToShow: 2,
          slidesToScroll: 2,
        },
      },
      {
        breakpoint: 640,
        settings: {
          slidesToShow: 1,
          slidesToScroll: 1,
        },
      },
    ],
  };

  const shuffleArray = (array) => {
    const shuffled = [...array];
    for (let i = shuffled.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
    }
    return shuffled;
  };

  const flattenedData = data
    .map((speaker) =>
      speaker.ytData
        ? speaker.ytData
            .filter((presentation) => !presentation.hideFromSlider)
            .map((presentation) => ({
              ...presentation,
              speakerName: speaker.name,
              speakerRole: speaker.role,
            }))
        : []
    )
    .flat();

  const shuffledData = shuffleArray(flattenedData);

  return (
    <section className={className}>
      <div className="container">
        <div className="row mb-4">
          <div className="col text-center">
            <span className="display-1 color-purple">{title}</span>
          </div>
        </div>
        <Slider {...settings} className="presentation-slider">
          {Array.isArray(shuffledData) ? (
            shuffledData.map((card, index) => (
              <a
                key={index}
                href={`https://www.youtube.com/watch?v=${card.id}`}
                target="_blank"
                rel="noopener noreferrer"
                className="slide"
              >
                <img className="img-fluid" src={card.img} alt={card.title} />
              </a>
            ))
          ) : (
            <p>No speakers available</p>
          )}
        </Slider>
      </div>
    </section>
  );
}
